import { useQuery } from '@tanstack/react-query';

import { useMemo, useState } from 'react';

import { AddCircle, Check, Clear, ContentCopy, Delete, Edit, Refresh } from '@mui/icons-material';
import { Box, Button, FormControlLabel, IconButton, Paper, Stack, Switch, TextField, Tooltip } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';

import { useApiClient, useIsMobile, useToastService } from '@@common';
import { ConfirmDialog, GalleryHeader, GalleryStatistics } from '@@components';

import { AdminClient, TenantModel, TenantSummaryModel, TenantSummaryModelPagingResultModel } from '../../../api/api';
import useDebouncedCallback from '../../../common/hooks/useDebouncedCallback';
import { usePersistedSetting } from '../../../common/hooks/usePersistedSetting';
import CreateTenantDialog from './CreateTenantDialog';
import EditTenantDialog from './EditTenantDialog';

const ACTIVE_ONLY_FILTER_KEY = 'tenants.activeOnly';
const FILTER_DEBOUNCE_MS = 300;
const pageSizeOptions = [20, 50];
const initialPaginationModel: GridPaginationModel = {
	pageSize: pageSizeOptions[0],
	page: 0,
};
const initialFilterModel = {
	items: [],
};
const initialSortModel: GridSortModel = [{ field: 'code', sort: 'asc' }];

const sortFieldName: Record<string, string> = {
	code: 'Code',
	name: 'Name',
	description: 'Description',
	isActive: 'IsActive',
	createdDate: 'CreatedDate',
};

export default function TenantsListView() {
	const isMobile = useIsMobile();
	const client = useApiClient(AdminClient);
	const toastService = useToastService();

	const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(initialPaginationModel);
	const [sortModel, setSortModel] = useState<GridSortModel>(initialSortModel);
	const [filterModel, setFilterModel] = useState<GridFilterModel>(initialFilterModel);
	const [codeFilter, setCodeFilter] = useState('');
	const [nameFilter, setNameFilter] = useState('');
	const [debouncedCodeFilter, setDebouncedCodeFilter] = useState('');
	const [debouncedNameFilter, setDebouncedNameFilter] = useState('');
	const [activeOnly, setActiveOnly] = usePersistedSetting<boolean>(ACTIVE_ONLY_FILTER_KEY, true);
	const normalizedCodeFilter = debouncedCodeFilter.trim();
	const normalizedNameFilter = debouncedNameFilter.trim();

	const updateCodeFilter = useDebouncedCallback((value: string) => {
		setDebouncedCodeFilter(value);
		setPaginationModel((current) => ({ ...current, page: 0 }));
	}, FILTER_DEBOUNCE_MS);

	const updateNameFilter = useDebouncedCallback((value: string) => {
		setDebouncedNameFilter(value);
		setPaginationModel((current) => ({ ...current, page: 0 }));
	}, FILTER_DEBOUNCE_MS);

	const { data, isLoading, refetch } = useQuery<TenantSummaryModelPagingResultModel>({
		queryKey: ['tenants', paginationModel, sortModel, filterModel, normalizedCodeFilter, normalizedNameFilter, activeOnly],
		queryFn: async () => {
			const sortBy = sortModel.length > 0 ? sortFieldName[sortModel[0].field] : undefined;
			const sortDirection = sortModel.length > 0 ? (sortModel[0].sort === 'asc' ? true : false) : true;
			return client.getTenants(
				normalizedCodeFilter || undefined,
				normalizedNameFilter || undefined,
				activeOnly,
				paginationModel.page,
				paginationModel.pageSize,
				sortBy,
				sortDirection,
			);
		},
	});

	const [openCreateDialog, setOpenCreateDialog] = useState(false);
	const [openEditDialog, setOpenEditDialog] = useState(false);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [selectedTenant, setSelectedTenant] = useState<TenantModel | null>(null);

	const rows = useMemo(() => data?.items ?? [], [data?.items]);

	const clearFilters = () => {
		setCodeFilter('');
		setNameFilter('');
		setDebouncedCodeFilter('');
		setDebouncedNameFilter('');
		setActiveOnly(true);
		setPaginationModel((current) => ({ ...current, page: 0 }));
	};

	const handleEditTenant = async (tenant: TenantSummaryModel) => {
		try {
			const fullTenant = await client.getTenantById(tenant.id);
			setSelectedTenant(fullTenant);
			setOpenEditDialog(true);
		} catch (error) {
			console.error('Error fetching tenant details:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to fetch tenant details: ${errorMessage}`);
		}
	};

	const handleDeleteTenant = async (tenant: TenantSummaryModel) => {
		try {
			const fullTenant = await client.getTenantById(tenant.id);
			setSelectedTenant(fullTenant);
			setOpenDeleteDialog(true);
		} catch (error) {
			console.error('Error fetching tenant details:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to fetch tenant details: ${errorMessage}`);
		}
	};

	const handleConfirmDelete = async () => {
		if (!selectedTenant) return;

		try {
			await client.deleteTenant(selectedTenant.id);
			toastService.success(`Tenant "${selectedTenant.code}" deleted successfully`);
			refetch();
		} catch (error) {
			console.error('Error deleting tenant:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to delete tenant: ${errorMessage}`);
		} finally {
			setOpenDeleteDialog(false);
			setSelectedTenant(null);
		}
	};

	const handleCancelDelete = () => {
		setOpenDeleteDialog(false);
		setSelectedTenant(null);
	};

	const handleCopyId = (tenant: TenantSummaryModel) => {
		navigator.clipboard.writeText(tenant.id);
		toastService.success('ID copied to clipboard');
	};

	const handleCopyCode = (tenant: TenantSummaryModel) => {
		navigator.clipboard.writeText(tenant.code);
		toastService.success('Code copied to clipboard');
	};

	const rowCount = useMemo(
		() => (data ? paginationModel.page * paginationModel.pageSize + (data.hasNextPage ? paginationModel.pageSize : rows.length) : 0),
		[paginationModel, data, rows.length],
	);

	const columns: GridColDef<TenantSummaryModel>[] = [
		{
			field: 'code',
			headerName: 'Code',
			sortable: true,
			width: isMobile ? 130 : 200,
		},
		{
			field: 'name',
			headerName: 'Name',
			sortable: true,
			flex: 1,
		},
		{
			field: 'description',
			headerName: 'Description',
			sortable: true,
			flex: 3,
		},
		{
			field: 'isActive',
			headerName: 'Active',
			sortable: true,
			width: 100,
			align: 'center',
			headerAlign: 'center',
			renderCell: (params) => (params.value ? <Check color="success" fontSize="small" /> : null),
		},
		{
			field: 'actions',
			type: 'actions',
			headerName: 'Actions',
			width: isMobile ? 64 : 120,
			getActions: (params) => [
				<GridActionsCellItem icon={<ContentCopy />} label="Copy ID" onClick={() => handleCopyId(params.row)} showInMenu />,
				<GridActionsCellItem icon={<ContentCopy />} label="Copy Code" onClick={() => handleCopyCode(params.row)} showInMenu />,
				<GridActionsCellItem icon={<Edit />} label="Edit" onClick={() => handleEditTenant(params.row)} showInMenu />,
				<GridActionsCellItem icon={<Delete />} label="Delete" onClick={() => handleDeleteTenant(params.row)} showInMenu />,
			],
		},
	];

	return (
		<Stack direction="column" spacing={2} sx={{ flexGrow: 1, minHeight: 0, minWidth: 0, p: (theme) => `${theme.layout.edgeGap}px`, overflow: 'hidden' }}>
			<GalleryHeader title="Tenants" subtitle="Manage tenant workspaces and their access." utilityActions={<Tooltip title="Refresh tenants"><IconButton onClick={() => refetch()}><Refresh /></IconButton></Tooltip>} primaryAction={<Button variant="contained" startIcon={<AddCircle />} onClick={() => setOpenCreateDialog(true)}>Create tenant</Button>} statistics={<GalleryStatistics items={[{ label: 'Tenants on this page', value: rows.length }, { label: 'Active', value: rows.filter((tenant) => tenant.isActive).length }]} />} />

			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				spacing={2}
				sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap', rowGap: 1, alignItems: { xs: 'stretch', sm: 'center' } }}
			>
				<TextField
					size="small"
					label="Code"
					value={codeFilter}
					onChange={(event) => {
						setCodeFilter(event.target.value);
						updateCodeFilter(event.target.value);
					}}
					sx={{ width: { xs: '100%', sm: 220 } }}
				/>
				<TextField
					size="small"
					label="Name"
					value={nameFilter}
					onChange={(event) => {
						setNameFilter(event.target.value);
						updateNameFilter(event.target.value);
					}}
					sx={{ width: { xs: '100%', sm: 260 } }}
				/>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'space-between', sm: 'initial' } }}>
					<FormControlLabel
						control={
							<Switch
								checked={activeOnly}
								onChange={(event) => {
									setActiveOnly(event.target.checked);
									setPaginationModel((current) => ({ ...current, page: 0 }));
								}}
								size="small"
							/>
						}
						label="Active Only"
					/>
					<IconButton aria-label="Clear filters" title="Clear filters" onClick={clearFilters}>
						<Clear />
					</IconButton>
				</Box>
			</Stack>

			<Paper sx={{ flexGrow: 1, flexShrink: 1, minHeight: 0, overflow: 'hidden' }}>
				<DataGrid
					loading={isLoading}
					rows={rows}
					rowCount={rowCount}
					columns={columns}
					getRowId={(row) => row.id}
					paginationMode="server"
					sortingMode="server"
					disableColumnFilter
					pageSizeOptions={pageSizeOptions}
					paginationModel={paginationModel}
					onPaginationModelChange={setPaginationModel}
					sortModel={sortModel}
					onSortModelChange={setSortModel}
					filterModel={filterModel}
					onFilterModelChange={setFilterModel}
					columnVisibilityModel={{ description: !isMobile, isActive: !isMobile }}
					density={isMobile ? 'compact' : 'standard'}
					sx={{ border: 0, minWidth: 0 }}
				/>
			</Paper>
			<CreateTenantDialog open={openCreateDialog} setOpen={setOpenCreateDialog} onSuccess={() => refetch()} />
			{selectedTenant && (
				<EditTenantDialog
					open={openEditDialog}
					setOpen={setOpenEditDialog}
					tenant={selectedTenant}
					onSuccess={() => {
						refetch();
						setSelectedTenant(null);
					}}
				/>
			)}
			<ConfirmDialog
				open={openDeleteDialog}
				title="Delete Tenant"
				message={
					selectedTenant
						? `Are you sure you want to delete tenant "${selectedTenant.code}"? This action cannot be undone and may affect any flows or operations that depend on it.`
						: ''
				}
				confirmText="Delete"
				confirmColor="error"
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>
		</Stack>
	);
}
